import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Typography,
  Alert,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
  Grid,
  TextField,
} from "@mui/material"
import { Refresh as RefreshIcon, CropFree as CropFreeIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material"
import { Chip } from "@mui/material"
import api from "../../services/api"
import HorseLoader from "../../components/loadingHorse"
import type { Stall } from "@/src/interfaces"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation, useAppNavigation } from "../../App"
import RoiEditorDialog from "../../components/RoiEditorDialog"
import type { RoiPoint } from "../../components/RoiEditor"

// Interface para os dados do formulário de câmera
interface CameraFormData {
  cameraId?: number
  cameraName: string
  cameraModel: string
  cameraChannel: string | number
  cameraStatus: number
  cameraRecorder: string | number
  cameraStall: string | number
  stableId: string | number
  /**
   * Região de interesse para a IA, em coordenadas normalizadas [0,1].
   * Persistida como JSON string. Campo ainda inexistente na API — o backend
   * deve criar a coluna `cameraRoi` (TEXT/JSON) para recebê-lo.
   */
  cameraRoi?: RoiPoint[] | null
  /** Bebedouro (`cameraRoiWater`) — mesma normalização da baia. */
  cameraRoiWater?: RoiPoint[] | null
  /** Comedouro (`cameraRoiFeed`). */
  cameraRoiFeed?: RoiPoint[] | null
}

/** Serializa uma região, ou null quando não definida (menos de 3 pontos). */
function serializarRoi(pts: RoiPoint[] | null | undefined): string | null {
  return pts && pts.length >= 3 ? JSON.stringify(pts) : null
}

/** Faz parse defensivo do cameraRoi vindo da API (string JSON ou array). */
function parseRoi(raw: any): RoiPoint[] {
  if (!raw) return []
  try {
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw
    if (Array.isArray(arr)) {
      return arr
        .filter((p) => p && typeof p.x === "number" && typeof p.y === "number")
        .map((p) => ({ x: p.x, y: p.y }))
    }
  } catch {
    /* ignora JSON inválido */
  }
  return []
}

// Interface para os dados do gravador
interface Recorder {
  value: number
  label: string
  channels: number // Número de canais disponíveis
}

export default function CameraForm() {
  const { t } = useTranslation()
  const { getParam } = useAppNavigation()
  const { navigateToPage } = useCryptoNavigation()
  
  // Obtém parâmetros do sistema criptografado
  const id = getParam('id')
  const mode = getParam('mode')
  const tenantId = getParam('tenantId') || '1'
  
  const isEditMode = mode === 'edit' && Boolean(id)
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()

  const [recorders, setRecorders] = useState<Recorder[]>([])
  const [allStalls, setAllStalls] = useState<Stall[]>([])
  const [filteredStalls, setFilteredStalls] = useState<{ value: number; label: string }[]>([])
  const [stables, setStables] = useState<{ value: number; label: string }[]>([])
  const [tenants, setTenants] = useState<{ value: number; label: string }[]>([])
  const [selectedStable, setSelectedStable] = useState<number | "">("")
  const [selectedTenant, setSelectedTenant] = useState<number | "">(Number(tenantId) || "")
  const [availableChannels, setAvailableChannels] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [debugInfo, setDebugInfo] = useState<any>(null)
  const [isLoadingCameraData, setIsLoadingCameraData] = useState(false)
  const [dataLoaded, setDataLoaded] = useState({
    tenants: false,
    recorders: false,
    stables: false,
    stalls: false,
    cameraData: false
  })

  // Estado para o formulário da camera
  const [cameraData, setCameraData] = useState<CameraFormData>({
    cameraName: "",
    cameraModel: "",
    cameraChannel: "",
    cameraStatus: 1,
    cameraRecorder: "",
    cameraStall: "",
    stableId: "",
    cameraRoi: null,
    cameraRoiWater: null,
    cameraRoiFeed: null,
  })

  const [roiDialogOpen, setRoiDialogOpen] = useState(false)

  const tenant_id = Number(tenantId)

  // Função para obter o número de canais disponíveis baseado no gravador selecionado
  const getSelectedRecorderChannels = () => {
    const selectedRecorder = recorders.find(r => r.value === Number(cameraData.cameraRecorder))
    return selectedRecorder ? selectedRecorder.channels : 0
  }

  // Função para gerar lista de canais disponíveis
  const generateAvailableChannels = (maxChannels: number) => {
    return Array.from({ length: maxChannels }, (_, i) => i + 1)
  }

  // Efeito para atualizar canais disponíveis quando o gravador for alterado
  useEffect(() => {
    if (cameraData.cameraRecorder && recorders.length > 0) {
      const maxChannels = getSelectedRecorderChannels()
      setAvailableChannels(generateAvailableChannels(maxChannels))
      
      // Em modo de edição, manter o canal atual se estiver dentro do limite
      if (isEditMode && cameraData.cameraChannel) {
        const currentChannel = Number(cameraData.cameraChannel)
        if (currentChannel > maxChannels || currentChannel < 1) {
          setCameraData(prev => ({
            ...prev,
            cameraChannel: ""
          }))
        }
      }
    } else {
      setAvailableChannels([])
      if (!isEditMode || !cameraData.cameraRecorder) {
        setCameraData(prev => ({
          ...prev,
          cameraChannel: ""
        }))
      }
    }
  }, [cameraData.cameraRecorder, recorders, isEditMode])

  // Função para forçar recarregamento dos dados
  const handleRetry = () => {
    setLoading(true)
    setError(null)
    setRetryCount((prev) => prev + 1)
    setDataLoaded({
      tenants: false,
      recorders: false,
      stables: false,
      stalls: false,
      cameraData: false
    })
  }

  // Carregar dados necessários (tenants, recorders, stables, stalls)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const currentTenantId = selectedTenant || Number(tenantId)
        
        // Carregar tenants
        try {
          const tenantsResponse = await api.get('/tenant')
          console.log("Resposta completa dos tenants:", tenantsResponse.data)
          
          const tenantsData = tenantsResponse.data.tenants || tenantsResponse.data

          if (Array.isArray(tenantsData)) {
            setTenants(
              tenantsData.map((tenant: any) => ({
                value: tenant.tenantId || tenant.id,
                label: tenant.tenantName || tenant.name || `Tenant ${tenant.tenantId || tenant.id}`,
              })),
            )
            console.log("Tenants carregados com sucesso")
          } else if (tenantsData && typeof tenantsData === "object") {
            // Se for um único objeto
            setTenants([
              {
                value: tenantsData.tenantId || tenantsData.id || 1,
                label: tenantsData.tenantName || tenantsData.name || "Tenant 1",
              },
            ])
          }
          setDataLoaded(prev => ({ ...prev, tenants: true }))
        } catch (tenantError) {
          console.error("Erro ao carregar tenants:", tenantError)
          // Usar tenant atual como padrão se não conseguir carregar
          setTenants([
            { value: Number(tenantId), label: `Tenant ${tenantId}` }
          ])
          setDataLoaded(prev => ({ ...prev, tenants: true }))
        }
        
        // Carregar gravadores
        try {
          const recordersResponse = await api.get(`/recorder/${currentTenantId}`)
          console.log("Resposta completa dos gravadores:", recordersResponse.data)
          
          const recordersData = recordersResponse.data.recorders || recordersResponse.data

          if (Array.isArray(recordersData)) {
            const processedRecorders = recordersData.map((recorder: any) => {
              console.log("Processando gravador:", recorder)
              
              // Tentar diferentes campos possíveis para o número de canais
              const channels = recorder.channels || 
                              recorder.maxChannels || 
                              recorder.recorderChannels || 
                              recorder.totalChannels ||
                              recorder.numChannels ||
                              16 // Padrão final
              
              return {
                value: recorder.recorderId || recorder.id,
                label: recorder.recorderName || recorder.name,
                channels: Number(channels)
              }
            })
            
            console.log("Gravadores processados:", processedRecorders)
            setRecorders(processedRecorders)
            console.log("Gravadores carregados com sucesso")
          } else if (recordersData && typeof recordersData === "object") {
            // Se for um único objeto
            console.log("Gravador único:", recordersData)
            
            const channels = recordersData.channels || 
                            recordersData.maxChannels || 
                            recordersData.recorderChannels || 
                            recordersData.totalChannels ||
                            recordersData.numChannels ||
                            16
            
            setRecorders([
              {
                value: recordersData.recorderId || recordersData.id || 1,
                label: recordersData.recorderName || recordersData.name || "Gravador 1",
                channels: Number(channels),
              },
            ])
          }
          setDataLoaded(prev => ({ ...prev, recorders: true }))
        } catch (recorderError) {
          console.error("Erro ao carregar gravadores:", recorderError)
          // Valores padrão com informação de canais
          setRecorders([
            { value: 1, label: "NVR 1", channels: 16 },
            { value: 2, label: "NVR 2", channels: 8 },
          ])
          console.log("Usando valores padrão para gravadores")
          setDataLoaded(prev => ({ ...prev, recorders: true }))
        }

        // Carregar estábulos
        try {
          const stablesResponse = await api.get(`/stable/${currentTenantId}`)
          const stablesData = stablesResponse.data.stables || stablesResponse.data

          if (Array.isArray(stablesData)) {
            setStables(
              stablesData.map((stable: any) => ({
                value: stable.stableId,
                label: stable.stableName,
              })),
            )
            console.log("Estábulos carregados com sucesso")
          } else {
            console.warn("Resposta da API para estábulos em formato inesperado:", stablesData)
            setStables([
              { value: 1, label: "Estábulo 1" },
              { value: 2, label: "Estábulo 2" },
            ])
            console.log("Usando valores padrão para estábulos")
          }
          setDataLoaded(prev => ({ ...prev, stables: true }))
        } catch (stableError) {
          console.error("Erro ao carregar estábulos:", stableError)
          setStables([
            { value: 1, label: "Estábulo 1" },
            { value: 2, label: "Estábulo 2" },
          ])
          setDataLoaded(prev => ({ ...prev, stables: true }))
        }

        // Carregar todas as baias
        try {
          const stallsResponse = await api.get(`/stall/${currentTenantId}`)
          const stallsData = stallsResponse.data.stalls || stallsResponse.data

          if (Array.isArray(stallsData)) {
            // Guardar todas as baias para filtragem posterior
            setAllStalls(
              stallsData.map((stall: any) => ({
                stallId: stall.stallId,
                stallName: stall.stallName,
                stableId: stall.stableId || 1, // Usar 1 como padrão se não tiver estábulo
              })),
            )
            console.log("Baias carregadas com sucesso")

            // Para depuração
            setDebugInfo({
              stallsData: stallsData.slice(0, 2), // Mostrar apenas as 2 primeiras baias para não poluir
              totalStalls: stallsData.length,
            })
          } else {
            console.warn("Resposta da API para baias em formato inesperado:", stallsData)
            setAllStalls([
              { stallId: 1, stallName: "Baia 1", stableId: 1 },
              { stallId: 2, stallName: "Baia 2", stableId: 1 },
              { stallId: 3, stallName: "Baia 3", stableId: 2 },
              { stallId: 4, stallName: "Baia 4", stableId: 2 },
            ])
          }
          setDataLoaded(prev => ({ ...prev, stalls: true }))
        } catch (stallError) {
          console.error("Erro ao carregar baias:", stallError)
          setAllStalls([
            { stallId: 1, stallName: "Baia 1", stableId: 1 },
            { stallId: 2, stallName: "Baia 2", stableId: 1 },
            { stallId: 3, stallName: "Baia 3", stableId: 2 },
            { stallId: 4, stallName: "Baia 4", stableId: 2 },
          ])
          setDataLoaded(prev => ({ ...prev, stalls: true }))
        }
      } catch (err) {
        console.error("Erro geral ao carregar dados:", err)
        setError(t("cameras.loadOptionsError"))
      }
    }

    fetchData()
  }, [retryCount, selectedTenant, tenantId])

  // Carregar dados da câmera para edição - SEPARADO e executado APÓS os dados base
  useEffect(() => {
    const fetchCameraData = async () => {
      if (!isEditMode || !id) return
      
      // Aguarda todos os dados base estarem carregados
      if (!dataLoaded.tenants || !dataLoaded.recorders || !dataLoaded.stables || !dataLoaded.stalls) {
        return
      }

      try {
        setIsLoadingCameraData(true)
        console.log("Carregando dados da câmera para edição...")
        
        const response = await api.get(`/camera/${selectedTenant || tenant_id}/${id}`)
        console.log("Dados da câmera recebidos:", response.data)

        const cameraDataResponse = response.data.camera || response.data

        // Carregar dados no estado
        const newCameraData = {
          cameraId: cameraDataResponse.cameraId,
          cameraName: cameraDataResponse.cameraName || "",
          cameraModel: cameraDataResponse.cameraModel || "",
          cameraChannel: cameraDataResponse.cameraChannel || "",
          cameraStatus: cameraDataResponse.cameraStatus !== undefined ? Number(cameraDataResponse.cameraStatus) : 1,
          cameraRecorder: cameraDataResponse.cameraRecorder !== undefined ? Number(cameraDataResponse.cameraRecorder) : "",
          cameraStall: cameraDataResponse.cameraStall !== undefined ? Number(cameraDataResponse.cameraStall) : "",
          stableId: cameraDataResponse.stableId !== undefined ? Number(cameraDataResponse.stableId) : "",
          cameraRoi: parseRoi(cameraDataResponse.cameraRoi),
          cameraRoiWater: parseRoi(cameraDataResponse.cameraRoiWater),
          cameraRoiFeed: parseRoi(cameraDataResponse.cameraRoiFeed),
        }
        
        console.log("Dados da câmera processados:", newCameraData)
        setCameraData(newCameraData)

        // Encontrar o estábulo baseado na baia selecionada
        if (newCameraData.cameraStall && allStalls.length > 0) {
          console.log("Procurando estábulo para a baia:", newCameraData.cameraStall)
          const stall = allStalls.find((s) => s.stallId === Number(newCameraData.cameraStall))
          console.log("Baia encontrada:", stall)
          
          if (stall) {
            console.log("Definindo estábulo selecionado:", stall.stableId)
            setSelectedStable(stall.stableId)
          }
        }
        // Se não encontrou pela baia, usar o stableId diretamente
        else if (newCameraData.stableId) {
          console.log("Definindo estábulo a partir dos dados diretos:", newCameraData.stableId)
          setSelectedStable(Number(newCameraData.stableId))
        }
        
        // Se temos tenantId nos dados da câmera, usar ele
        if (cameraDataResponse.tenantId) {
          console.log("Definindo tenant a partir dos dados da câmera:", cameraDataResponse.tenantId)
          setSelectedTenant(Number(cameraDataResponse.tenantId))
        }

        setDataLoaded(prev => ({ ...prev, cameraData: true }))
      } catch (error) {
        console.error("Erro ao carregar dados da câmera:", error)
        setError(t("cameras.loadCameraError"))
      } finally {
        setIsLoadingCameraData(false)
      }
    }

    fetchCameraData()
  }, [isEditMode, id, selectedTenant, tenant_id, dataLoaded.tenants, dataLoaded.recorders, dataLoaded.stables, dataLoaded.stalls, allStalls])

  // Controle de loading final
  useEffect(() => {
    const allDataLoaded = dataLoaded.tenants && dataLoaded.recorders && dataLoaded.stables && dataLoaded.stalls
    const cameraDataNotNeeded = !isEditMode
    const cameraDataLoaded = isEditMode ? dataLoaded.cameraData : true
    
    if (allDataLoaded && (cameraDataNotNeeded || cameraDataLoaded)) {
      setLoading(false)
    }
  }, [dataLoaded, isEditMode])

  // Efeito para recarregar dados quando o tenant for alterado
  useEffect(() => {
    if (selectedTenant && !isEditMode) {
      // Limpar seleções quando trocar de tenant
      setCameraData(prev => ({
        ...prev,
        cameraRecorder: "",
        cameraChannel: "",
        cameraStall: ""
      }))
      setSelectedStable("")
      setFilteredStalls([])
      setAvailableChannels([])
    }
  }, [selectedTenant, isEditMode])

  // Efeito para filtrar baias quando um estábulo é selecionado
  useEffect(() => {
    if (selectedStable && allStalls.length > 0) {
      const filtered = allStalls
        .filter((stall) => stall.stableId === Number(selectedStable))
        .map((stall) => ({
          value: stall.stallId,
          label: stall.stallName,
        }))

      console.log("Baias filtradas para estábulo", selectedStable, ":", filtered)
      setFilteredStalls(filtered)

      // Em modo de edição, verificar se a baia atual pertence ao estábulo
      if (isEditMode && cameraData.cameraStall) {
        const stallExists = filtered.some((s) => s.value === Number(cameraData.cameraStall))
        if (!stallExists && dataLoaded.cameraData) {
          // Só limpar se já carregamos os dados da câmera e a baia não pertence ao estábulo
          console.log("Baia atual não pertence ao estábulo selecionado")
          setCameraData((prev: CameraFormData) => ({
            ...prev,
            cameraStall: "",
          }))
        }
      }
    } else {
      setFilteredStalls([])
      
      // Limpar seleção de baia apenas se não estivermos carregando dados da câmera
      if (!isLoadingCameraData && !isEditMode) {
        setCameraData((prev: CameraFormData) => ({
          ...prev,
          cameraStall: "",
        }))
      }
    }
  }, [selectedStable, allStalls, isEditMode, cameraData.cameraStall, dataLoaded.cameraData, isLoadingCameraData])

  // Função para lidar com a mudança de tenant
  const handleTenantChange = (e: SelectChangeEvent<number | string>) => {
    const value = e.target.value
    console.log("Mudança de tenant:", value)
    
    setSelectedTenant(value as number | "")
  }

  // Função para lidar com a mudança de estábulo
  const handleStableChange = (e: SelectChangeEvent<number | string>) => {
    const value = e.target.value
    console.log("Mudança de estábulo:", value)
    
    setSelectedStable(value as number | "")

    // Atualizar o estado da câmera
    setCameraData((prev: CameraFormData) => ({
      ...prev,
      stableId: value,
      // Limpar a baia quando trocar de estábulo
      cameraStall: ""
    }))
  }

  // Função para lidar com mudanças nos campos da câmera
  const handleCameraChange = (name: string, value: any) => {
    setCameraData((prev: CameraFormData) => ({
      ...prev,
      [name]: value,
    }))
  }
// Função para verificar se gravador+canal já está em uso
  const checkChannelAvailability = async (recorderId: number, channel: number, currentCameraId?: number) => {
    try {
      const response = await api.get(`/camera/${selectedTenant || tenant_id}`)
      const camerasData = response.data.cameras || response.data || []
      
      if (Array.isArray(camerasData)) {
        const conflictCamera = camerasData.find((cam: any) => 
          Number(cam.cameraRecorder) === recorderId && 
          Number(cam.cameraChannel) === channel &&
          (currentCameraId ? Number(cam.cameraId) !== currentCameraId : true)
        )
        
        if (conflictCamera) {
          return {
            available: false,
            conflictCamera: {
              id: conflictCamera.cameraId,
              name: conflictCamera.cameraName
            }
          }
        }
      }
      
      return { available: true }
    } catch (error) {
      console.warn("Erro ao verificar disponibilidade do canal:", error)
      // Em caso de erro na verificação, permitir continuar
      return { available: true }
    }
  }

  // Função para salvar a câmera
  const handleSave = async () => {
    console.log("Iniciando salvamento da câmera...")
    console.log("Dados atuais do formulário:", cameraData)
    
    // Validar dados
    if (!selectedTenant) {
      setError(t("cameras.ownerRequired"))
      return
    }

    if (!cameraData.cameraName?.trim()) {
      setError(t("cameras.nameRequired"))
      return
    }

    if (!cameraData.cameraChannel || cameraData.cameraChannel === "") {
      setError(t("cameras.channelRequired"))
      return
    }

    if (!cameraData.cameraRecorder || cameraData.cameraRecorder === "") {
      setError(t("cameras.recorderRequired"))
      return
    }

    if (!cameraData.cameraStall || cameraData.cameraStall === "") {
      setError(t("cameras.stallRequired"))
      return
    }

    if (!selectedStable) {
      setError(t("cameras.stableRequired"))
      return
    }

    // Validar se o canal está dentro do limite permitido
    const maxChannels = getSelectedRecorderChannels()
    const channelNumber = Number(cameraData.cameraChannel)
    if (channelNumber < 1 || channelNumber > maxChannels) {
      setError(t("cameras.channelRange", { max: maxChannels }))
      return
    }
// Verificar se o gravador+canal já está em uso (exceto para a própria câmera em edição)
    const recorderId = Number(cameraData.cameraRecorder)
    const currentCameraId = isEditMode ? Number(id) : undefined
    
    console.log("Verificando disponibilidade do canal...")
    const channelCheck = await checkChannelAvailability(recorderId, channelNumber, currentCameraId)
    
    if (!channelCheck.available && channelCheck.conflictCamera) {
      const recorderName = recorders.find(r => r.value === recorderId)?.label || t("cameras.recorderFallback", { id: recorderId })
      setError(
        t("cameras.channelInUse", {
          channel: channelNumber,
          recorder: recorderName,
          camera: channelCheck.conflictCamera.name,
        })
      )
      return
    }
    try {
      setLoading(true)

      // Preparar dados para envio
      const dataToSend = {
        cameraName: cameraData.cameraName.trim(),
        cameraModel: cameraData.cameraModel?.trim() || "",
        cameraChannel: Number(cameraData.cameraChannel),
        cameraStatus: Number(cameraData.cameraStatus),
        cameraRecorder: Number(cameraData.cameraRecorder),
        cameraStall: Number(cameraData.cameraStall),
        stableId: Number(selectedStable || cameraData.stableId),
        tenantId: Number(selectedTenant),
        // As três regiões, normalizadas como JSON (null quando não definidas).
        cameraRoi: serializarRoi(cameraData.cameraRoi),
        cameraRoiWater: serializarRoi(cameraData.cameraRoiWater),
        cameraRoiFeed: serializarRoi(cameraData.cameraRoiFeed),
      }

      console.log("Dados a serem enviados:", dataToSend)

      let response
      if (isEditMode) {
        response = await api.put(`/camera/${selectedTenant}/${id}`, dataToSend)
      } else {
        response = await api.post(`/camera/${selectedTenant}`, dataToSend)
      }

      console.log("Câmera salva com sucesso:", response.data)
      
      // Navega de volta para a lista
      navigateToPage('cameras', { mode: 'list', tenantId: tenantId })
    } catch (error: any) {
      console.error("Erro ao salvar câmera:", error)
      
      let errorMsg = t("cameras.saveError")
      
      // Tratamento específico para erro de duplicata
      if (error.response?.status === 500 && error.response?.data) {
        const errorData = error.response.data
        const errorStr = typeof errorData === 'string' ? errorData : JSON.stringify(errorData)
        
        // Detectar erro de duplicata MySQL
        if (errorStr.includes('Duplicate entry') && errorStr.includes('camera_idxU')) {
          const recorderName = recorders.find(r => r.value === recorderId)?.label || t("cameras.recorderFallback", { id: recorderId })
          errorMsg = t("cameras.channelInUseDuplicate", { channel: channelNumber, recorder: recorderName })
        }
        // Detectar outros erros MySQL comuns
        else if (errorStr.includes('Foreign key constraint')) {
          errorMsg = t("cameras.foreignKeyError")
        }
        // Detectar erro de campo obrigatório
        else if (errorStr.includes('cannot be null') || errorStr.includes('required')) {
          errorMsg = t("cameras.requiredFieldsBlank")
        }
        // Usar mensagem do servidor se disponível
        else if (error.response?.data?.message) {
          errorMsg = error.response.data.message
        }
        else {
          errorMsg = t("cameras.serverError", { detail: errorStr.substring(0, 200) })
        }
      }
      // Tratamento para outros códigos de erro HTTP
      else if (error.response?.status === 400) {
        errorMsg = error.response?.data?.message || t("cameras.invalidData")
      }
      else if (error.response?.status === 404) {
        errorMsg = t("cameras.resourceNotFound")
      }
      else if (error.response?.status === 403) {
        errorMsg = t("cameras.noPermission")
      }
      else if (error.response?.data?.message) {
        errorMsg = error.response.data.message
      }
      else if (error.response?.data) {
        const errorData = typeof error.response.data === 'string' ? 
          error.response.data : 
          JSON.stringify(error.response.data)
        errorMsg = errorData.length > 200 ? 
          `${errorData.substring(0, 200)}...` : 
          errorData
      }
      
      setError(`${errorMsg}`)
    } finally {
      setLoading(false)
    }
  }

  // Função para cancelar e voltar
  const handleCancel = () => {
    navigateToPage('cameras', { mode: 'list', tenantId: tenantId })
  }

  // Determinar o número de colunas com base no tamanho da tela
  const getGridSize = () => {
    if (isMobile) return 12
    if (isTablet) return 6
    return 6
  }

  if (loading || isLoadingCameraData) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 4 }}>
        {HorseLoader ? <HorseLoader /> : <CircularProgress />}
      </Box>
    )
  }

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
        {isEditMode ? t("cameras.editTitle") : t("cameras.newTitle")}
      </Typography>

      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={handleRetry}>
              {t("cameras.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box component="form" noValidate autoComplete="off">
        <Grid container spacing={2}>
          <Grid item xs={getGridSize()} md={6}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>{t("fields.owner")}</InputLabel>
              <Select
                value={selectedTenant}
                onChange={handleTenantChange}
                label={t("fields.owner")}
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.value} value={tenant.value}>
                    {tenant.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={getGridSize()} md={6}>
            <TextField
              fullWidth
              label={t("cameras.nameLabel")}
              value={cameraData.cameraName}
              onChange={(e) => handleCameraChange("cameraName", e.target.value)}
              required
              margin="normal"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={getGridSize()} md={6}>
            <TextField
              fullWidth
              label={t("cameras.modelLabel")}
              value={cameraData.cameraModel}
              onChange={(e) => handleCameraChange("cameraModel", e.target.value)}
              margin="normal"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={getGridSize()} md={6}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>{t("fields.recorder")}</InputLabel>
              <Select
                value={cameraData.cameraRecorder}
                onChange={(e) => handleCameraChange("cameraRecorder", e.target.value)}
                label={t("fields.recorder")}
              >
                {recorders.map((recorder) => (
                  <MenuItem key={recorder.value} value={recorder.value}>
                    {recorder.label} ({t("cameras.channelsCount", { count: recorder.channels })})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={getGridSize()} md={6}>
            <FormControl 
              fullWidth 
              margin="normal" 
              required 
              disabled={!cameraData.cameraRecorder || availableChannels.length === 0}
            >
              <InputLabel>{t("fields.channel")}</InputLabel>
              <Select
                value={cameraData.cameraChannel}
                onChange={(e) => handleCameraChange("cameraChannel", e.target.value)}
                label={t("fields.channel")}
              >
                {availableChannels.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {t("cameras.channelOption", { channel })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {cameraData.cameraRecorder && (
              <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                {t("cameras.channelsAvailable", { max: getSelectedRecorderChannels() })}
              </Typography>
            )}
          </Grid>

          <Grid item xs={getGridSize()} md={6}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>{t("fields.status")}</InputLabel>
              <Select
                value={cameraData.cameraStatus}
                onChange={(e) => handleCameraChange("cameraStatus", e.target.value)}
                label={t("fields.status")}
              >
                <MenuItem value={1}>{t("common.active")}</MenuItem>
                <MenuItem value={0}>{t("common.inactive")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={getGridSize()} md={6}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>{t("fields.stable")}</InputLabel>
              <Select value={selectedStable} onChange={handleStableChange} label={t("fields.stable")}>
                {stables.map((stable) => (
                  <MenuItem key={stable.value} value={stable.value}>
                    {stable.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={getGridSize()} md={6}>
            <FormControl fullWidth margin="normal" required disabled={!selectedStable || filteredStalls.length === 0}>
              <InputLabel>{t("stalls.nameLabel")}</InputLabel>
              <Select
                value={cameraData.cameraStall}
                onChange={(e) => handleCameraChange("cameraStall", e.target.value)}
                label={t("stalls.nameLabel")}
              >
                {filteredStalls.map((stall) => (
                  <MenuItem key={stall.value} value={stall.value}>
                    {stall.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedStable && filteredStalls.length === 0 && (
              <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                {t("cameras.noStallsForStable")}
              </Typography>
            )}
          </Grid>

          {/* Região de interesse para a IA */}
          <Grid item xs={12}>
            <Box
              sx={{
                mt: 1,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="subtitle2">{t("cameras.roiHeading")}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("cameras.roiDescription")}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {cameraData.cameraRoi && cameraData.cameraRoi.length >= 3 ? (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleIcon />}
                      label={t("cameras.roiDefined", { count: cameraData.cameraRoi.length })}
                      onDelete={() => handleCameraChange("cameraRoi", null)}
                    />
                  ) : (
                    <Chip size="small" variant="outlined" label={t("cameras.roiNone")} />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Button
                  variant="outlined"
                  startIcon={<CropFreeIcon />}
                  onClick={() => setRoiDialogOpen(true)}
                  disabled={!cameraData.cameraRecorder || !cameraData.cameraChannel}
                  sx={{ width: isMobile ? "100%" : "auto" }}
                >
                  {cameraData.cameraRoi && cameraData.cameraRoi.length >= 3
                    ? t("cameras.roiEdit")
                    : t("cameras.roiDefine")}
                </Button>
                {(!cameraData.cameraRecorder || !cameraData.cameraChannel) && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t("cameras.roiSelectFirst")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0, width: isMobile ? "100%" : "auto" }}
            disabled={loading || isLoadingCameraData}
          >
            {t("cameras.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || isLoadingCameraData}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            {isEditMode ? t("cameras.update") : t("cameras.save")}
          </Button>
        </Box>
      </Box>

      {roiDialogOpen && (
        <RoiEditorDialog
          open={roiDialogOpen}
          onClose={() => setRoiDialogOpen(false)}
          onSave={(regioes) => {
            handleCameraChange("cameraRoi", regioes.roi)
            handleCameraChange("cameraRoiWater", regioes.water)
            handleCameraChange("cameraRoiFeed", regioes.feed)
          }}
          tenantId={Number(selectedTenant || tenant_id)}
          recorderId={Number(cameraData.cameraRecorder)}
          channel={Number(cameraData.cameraChannel)}
          cameraName={cameraData.cameraName || undefined}
          initialPoints={cameraData.cameraRoi || []}
          initialWater={cameraData.cameraRoiWater || []}
          initialFeed={cameraData.cameraRoiFeed || []}
        />
      )}
    </Paper>
  )
}